import { useState, useEffect } from 'react';
import { FaTimes, FaSpinner, FaBuilding, FaChartBar } from 'react-icons/fa';
import { getCompanyGroup, CnpjSpotCompany, CnpjSpotGroupResponse, searchPartnerCompanies, PartnerCompanyResult } from '../../../services/CnpjSpotService';
import { getPafAnalysis, PafAnalysisData } from '../../../services/CreditAnalysisService';
import appConfig from '../../../config/app-config';

const { address: CAE_API_URL, key: CAE_API_KEY } = appConfig.apis.creditAnalysisApiEnv;

interface Props {
    batchId: string;
    itemId: string;
    document: string;
    draweeName?: string;
    onClose: () => void;
}

type Tab = 'cadastro' | 'financeiro' | 'empresas';

const maskCnpj = (cnpj: string) => {
    const d = cnpj.replace(/\D/g, '');
    if (d.length !== 14) return cnpj;
    return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
};

const fmtCurrency = (v: number | null | undefined) => {
    if (v == null) return '-';
    return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const renderField = (label: string, value: string | number | null | undefined) => (
    <div className="py-1.5 border-b border-gray-100 dark:border-slate-600">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-sm text-gray-900 dark:text-gray-100">{value != null && value !== '' ? String(value) : <span className="text-gray-400 italic">-</span>}</div>
    </div>
);

// ── Company Card ──
function CompanyCard({ company, badge, borderColor }: { company: CnpjSpotCompany; badge: string; borderColor: string }) {
    const endereco = [company.logradouro, company.numero, company.complemento].filter(Boolean).join(', ');
    const cidadeUf = [company.municipio, company.uf].filter(Boolean).join('/');
    return (
        <div className={`border-2 rounded p-4 ${borderColor}`}>
            <span className="text-xs font-bold uppercase mb-2 block">{badge}</span>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0">
                {renderField('CNPJ', maskCnpj(company.cnpj))}
                {renderField('Razão Social', company.razao_social)}
                {renderField('Nome Fantasia', company.nome_fantasia)}
                {renderField('Situação', company.descricao_situacao_cadastral)}
                {renderField('Início Atividade', company.data_inicio_atividade)}
                {renderField('Capital Social', fmtCurrency(company.capital_social))}
                {renderField('Porte', company.porte)}
                {renderField('Natureza Jurídica', company.natureza_juridica)}
                {renderField('CNAE Principal', company.cnae_fiscal_descricao)}
                {renderField('Endereço', `${endereco}${company.bairro ? ` — ${company.bairro}` : ''}`)}
                {renderField('Cidade/UF', cidadeUf)}
                {renderField('CEP', company.cep)}
                {company.ddd_telefone_1 && renderField('Telefone', company.ddd_telefone_1)}
                {company.email && renderField('E-mail', company.email)}
            </div>
            {company.qsa && company.qsa.length > 0 && (
                <div className="mt-3">
                    <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase mb-1">Sócios ({company.qsa.length})</div>
                    <table className="w-full text-xs">
                        <thead><tr className="border-b text-gray-500 dark:text-gray-400">
                            <th className="text-left py-1">Nome</th><th className="text-left py-1">CPF/CNPJ</th><th className="text-left py-1">Qualificação</th>
                        </tr></thead>
                        <tbody>{company.qsa.map((s, i) => (
                            <tr key={i} className="border-b border-gray-100 dark:border-slate-600">
                                <td className="py-1 text-gray-900 dark:text-gray-100">{s.nome_socio}</td>
                                <td className="py-1 font-mono text-gray-700 dark:text-gray-300">{s.cnpj_cpf_do_socio || '-'}</td>
                                <td className="py-1 text-gray-700 dark:text-gray-300">{s.qualificacao_socio || '-'}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── PAF Financial Section (reuse logic from PafAnalysisModal) ──
const pctFields = ['mgEbit', 'mgLiquida', 'roa', 'roe', 'divLiqrecLiq', 'endividamentoTotalpl', 'permanentepl', 'plat', 'arvendas', 'estoquesvendas'];

function fmtValue(value: string | undefined, key: string): string {
    if (!value || value === '-') return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    if (pctFields.includes(key)) return (num * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 }) + '%';
    if (key === 'liqCorrente') return num.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'x';
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function calcLimits(data: Record<string, string>) {
    const divBruta = parseFloat(data.divBrutaRmil || '0') || 0;
    const recLiq = parseFloat(data.recliq || '0') || 0;
    const forn = parseFloat(data.forn || '0') || 0;
    const pl = parseFloat(data.pl || '0') || 0;
    return {
        lcCedente: Math.max(0, (divBruta * 0.10 + recLiq * 0.07 + forn * 0.10 + pl * 0.50) / 4),
        lcSacado: Math.max(0, (divBruta * 0.05 + recLiq * 0.05 + forn * 0.05 + pl * 0.05) / 4),
    };
}

function formatClosingDate(d: string | null): string {
    if (!d) return '-';
    const parts = d.split('-');
    return parts.length === 3 ? `${parts[1]}/${parts[0]}` : d;
}

type Field = { key: string; label: string; highlight?: boolean; calc?: (d: Record<string, string>) => string };

const BALANCE: Field[] = [
    { key: 'caixa', label: 'Caixa' },
    { key: 'ar', label: 'Contas à Receber' },
    { key: 'estoques', label: 'Estoques' },
    { key: '_occ_a', label: 'Outras Contas Circ.', calc: d => String(Math.round((parseFloat(d.ac||'0')||0) - (parseFloat(d.caixa||'0')||0) - (parseFloat(d.ar||'0')||0) - (parseFloat(d.estoques||'0')||0))) },
    { key: 'ac', label: 'Ativo Circulante', highlight: true },
    { key: '_onc_a', label: 'Outras Contas NC', calc: d => String(Math.round((parseFloat(d.at||'0')||0) - (parseFloat(d.ac||'0')||0) - (parseFloat(d.permanenteLiquido||'0')||0))) },
    { key: 'permanenteLiquido', label: 'Permanente' },
    { key: 'at', label: 'Ativo Total', highlight: true },
];
const PASSIVO: Field[] = [
    { key: 'bancosCp', label: 'Bancos CP' },
    { key: 'forn', label: 'Fornecedores' },
    { key: '_occ_p', label: 'Outras Contas Circ.', calc: d => String(Math.round((parseFloat(d.pc||'0')||0) - (parseFloat(d.bancosCp||'0')||0) - (parseFloat(d.forn||'0')||0))) },
    { key: 'pc', label: 'Passivo Circ.', highlight: true },
    { key: 'bancosLp', label: 'Bancos LP' },
    { key: '_onc_p', label: 'Outras Contas NC', calc: d => String(Math.round((parseFloat(d.at||'0')||0) - (parseFloat(d.pc||'0')||0) - (parseFloat(d.bancosLp||'0')||0) - (parseFloat(d.pl||'0')||0))) },
    { key: 'cs', label: 'Capital Social', highlight: true },
    { key: 'pl', label: 'Patrimônio Líquido', highlight: true },
    { key: 'at', label: 'Passivo Total', highlight: true },
];
const DRE: Field[] = [
    { key: 'recliq', label: 'Receita Líquida' },
    { key: 'ebit', label: 'EBIT', highlight: true },
    { key: 'mgEbit', label: 'Mg EBIT' },
    { key: 'resultLiq', label: 'Resultado Líq.', highlight: true },
    { key: 'mgLiquida', label: 'Mg Líquida' },
];
const INDICADORES: Field[] = [
    { key: 'divBrutaRmil', label: 'Dív. Bruta R$mil' },
    { key: 'divLiqRmil', label: 'Dív. Líq. R$mil' },
    { key: 'endividamentoTotalpl', label: 'Endivid. Total/PL' },
    { key: 'divLiqrecLiq', label: 'Dív Líq./Rec. Líq.' },
    { key: 'plat', label: 'AT/PL' },
    { key: 'permanentepl', label: 'Permanente/PL' },
    { key: 'liqCorrente', label: 'Liq. Corrente' },
    { key: 'arvendas', label: 'AR/Vendas' },
    { key: 'estoquesvendas', label: 'Estoques/Vendas' },
    { key: 'roa', label: 'ROA' },
    { key: 'roe', label: 'ROE' },
];

function FinTable({ title, fields, periods, zebra }: { title: string; fields: Field[]; periods: PafAnalysisData['periods']; zebra?: boolean }) {
    return (
        <div>
            <h4 className="font-semibold text-sm mb-1 text-gray-700 dark:text-gray-300">{title}</h4>
            <table className="w-full text-xs">
                <thead><tr className="border-b dark:border-gray-600">
                    <th className="text-left py-1 pr-2"></th>
                    {periods.map((p, i) => <th key={i} className="text-right py-1 px-1 whitespace-nowrap">{formatClosingDate(p.closingDate)}</th>)}
                </tr></thead>
                <tbody>{fields.map((f, idx) => (
                    <tr key={f.key} className={`border-b border-gray-100 dark:border-gray-700 ${f.highlight ? 'bg-gray-100 dark:bg-gray-700' : zebra && idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800' : ''}`}>
                        <td className={`py-0.5 pr-2 whitespace-nowrap ${f.highlight ? 'font-semibold text-gray-800 dark:text-gray-200' : 'text-gray-600 dark:text-gray-400'}`}>{f.label}</td>
                        {periods.map((p, i) => {
                            const val = f.calc ? f.calc(p.data) : p.data[f.key];
                            return <td key={i} className={`py-0.5 px-1 text-right font-mono ${f.highlight ? 'font-bold' : ''}`}>{fmtValue(val, f.key)}</td>;
                        })}
                    </tr>
                ))}</tbody>
            </table>
        </div>
    );
}

function FinancialTab({ paf }: { paf: PafAnalysisData }) {
    if (!paf.periods || paf.periods.length === 0) return <div className="p-8 text-center text-gray-500">Sem dados PAF disponíveis.</div>;
    // Sort periods: oldest first (left) → newest last (right). Null dates go to the end.
    const sortedPeriods = [...paf.periods].sort((a, b) => {
        const da = a.closingDate || '';
        const db = b.closingDate || '';
        if (!da && db) return 1;
        if (da && !db) return -1;
        return da.localeCompare(db);
    });
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-gray-50 dark:bg-gray-900 rounded p-3">
                <div><span className="text-gray-500">Grupo:</span> {paf.generalData.economicGroup || '-'}</div>
                <div><span className="text-gray-500">Cidade:</span> {paf.generalData.city}/{paf.generalData.state}</div>
                <div><span className="text-gray-500">Rating:</span> <span className="font-bold">{paf.generalData.rating || '-'}</span></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FinTable title="ATIVO" fields={BALANCE} periods={sortedPeriods} />
                <FinTable title="PASSIVO" fields={PASSIVO} periods={sortedPeriods} />
                <div className="space-y-4">
                    <FinTable title="DRE" fields={DRE} periods={sortedPeriods} />
                    <div>
                        <h4 className="font-semibold text-sm mb-1 text-gray-700 dark:text-gray-300">LIMITES TEÓRICOS R$mil</h4>
                        <table className="w-full text-xs">
                            <thead><tr className="border-b dark:border-gray-600">
                                <th className="text-left py-1 pr-2"></th>
                                {sortedPeriods.map((p, i) => <th key={i} className="text-right py-1 px-1 whitespace-nowrap">{formatClosingDate(p.closingDate)}</th>)}
                            </tr></thead>
                            <tbody>
                                <tr className="border-b border-gray-100 dark:border-gray-700">
                                    <td className="py-0.5 pr-2 text-gray-600 dark:text-gray-400">LC Cedente</td>
                                    {sortedPeriods.map((p, i) => <td key={i} className="py-0.5 px-1 text-right font-mono">{calcLimits(p.data).lcCedente.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</td>)}
                                </tr>
                                <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-700">
                                    <td className="py-0.5 pr-2 font-semibold text-gray-800 dark:text-gray-200">LC Sacado</td>
                                    {sortedPeriods.map((p, i) => <td key={i} className="py-0.5 px-1 text-right font-mono font-bold">{calcLimits(p.data).lcSacado.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</td>)}
                                </tr>
                                <tr className="border-b border-gray-100 dark:border-gray-700">
                                    <td className="py-0.5 pr-2 text-gray-600 dark:text-gray-400">Exposição Total</td>
                                    <td className="py-0.5 px-1 text-right font-mono" colSpan={sortedPeriods.length}>{paf.exposure != null ? paf.exposure.toLocaleString('pt-BR', { maximumFractionDigits: 0 }) : 'Sem Exposição'}</td>
                                </tr>
                                <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-700">
                                    <td className="py-0.5 pr-2 font-semibold text-gray-800 dark:text-gray-200">% Exposição/LC Sacado</td>
                                    <td className="py-0.5 px-1 text-right font-mono font-bold" colSpan={sortedPeriods.length}>{(() => {
                                        if (!paf.exposure) return '-';
                                        const { lcSacado } = calcLimits(sortedPeriods[sortedPeriods.length - 1].data);
                                        return lcSacado > 0 ? ((paf.exposure / lcSacado) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%' : '-';
                                    })()}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <FinTable title="INDICADORES" fields={INDICADORES} periods={sortedPeriods} zebra />
            </div>
            <div>
                <h4 className="font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300">PARECER</h4>
                {paf.generalData.creditOpinion ? (
                    <div className="text-sm border-l-2 border-blue-400 pl-3 py-1"><p className="text-gray-700 dark:text-gray-300">{paf.generalData.creditOpinion}</p></div>
                ) : sortedPeriods.filter(p => p.data?.parecerDeCredito).length > 0 ? (
                    <div className="space-y-2">
                        {sortedPeriods.filter(p => p.data?.parecerDeCredito).map((p, i) => (
                            <div key={i} className="text-sm border-l-2 border-blue-400 pl-3 py-1">
                                <p className="text-gray-700 dark:text-gray-300">{p.data.parecerDeCredito}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">Nenhum parecer disponível.</p>
                )}
            </div>
            {paf.references.length > 0 ? (
                <div>
                    <h4 className="font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300">REFERÊNCIAS COMERCIAIS</h4>
                    <div className="space-y-2">
                        {paf.references.map((ref, i) => (
                            <div key={i} className="text-sm border-l-2 border-blue-400 pl-3 py-1">
                                <div className="flex gap-3 text-gray-500 text-xs"><span>{ref.date || '-'}</span><span className="font-medium">{ref.source}</span></div>
                                <p className="text-gray-700 dark:text-gray-300">{ref.comment}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div>
                    <h4 className="font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300">REFERÊNCIAS COMERCIAIS</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">Nenhuma referência comercial disponível.</p>
                </div>
            )}
        </div>
    );
}

export default function UnifiedDraweeModal({ batchId, itemId, document, draweeName, onClose }: Props) {
    const cleanDoc = document.replace(/\D/g, '');
    const isCPF = cleanDoc.length === 11;
    const root = cleanDoc.substring(0, 8);

    const [tab, setTab] = useState<Tab>(cleanDoc.length === 11 ? 'cadastro' : 'financeiro');
    const [pjData, setPjData] = useState<CnpjSpotGroupResponse | null>(null);
    const [pafData, setPafData] = useState<PafAnalysisData | null>(null);
    const [pfRegistration, setPfRegistration] = useState<any>(null);
    const [partnerCompanies, setPartnerCompanies] = useState<PartnerCompanyResult[] | null>(null);
    const [loadingPartners, setLoadingPartners] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showBranches, setShowBranches] = useState(false);

    useEffect(() => {
        setLoading(true);
        if (isCPF) {
            // PF: fetch bureau details (AgriScore/Serasa registration)
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (CAE_API_KEY) headers['x-api-key'] = CAE_API_KEY;
            fetch(`${CAE_API_URL}/analysis-items/${itemId}/bureau-details`, { headers })
                .then(res => res.json())
                .then(data => {
                    const bureaus = data.bureaus || [];
                    // Try AgriScore first, then Serasa
                    const agri = bureaus.find((b: any) => (b.bureau_name || b.bureau || '').toUpperCase().includes('ACRISCORE'));
                    const serasa = bureaus.find((b: any) => (b.bureau_name || b.bureau || '').toUpperCase().includes('SERASA'));
                    const source = agri || serasa;
                    if (!source) return;
                    const sourceData = source.data || {};
                    const registration = sourceData.registration || sourceData.reports?.[0]?.registration;
                    const optScore = sourceData.optionalFeatures?.score || sourceData.reports?.[0]?.score;
                    const score = optScore
                        ? { score: optScore.score, defaultRate: optScore.defaultRate, scoreClass: optScore.scoreClass || optScore.range }
                        : sourceData.score !== undefined
                            ? { score: sourceData.score, defaultRate: sourceData.defaultRate, scoreClass: sourceData.range }
                            : null;
                    if (registration) {
                        setPfRegistration({ ...registration, _bureau: agri ? 'AgroScore' : 'Serasa', _score: score });
                    } else {
                        setPfRegistration({ _bureau: agri ? 'AgroScore' : 'Serasa', _raw: sourceData });
                    }
                })
                .catch(() => {})
                .finally(() => setLoading(false));
        } else {
            // PJ: fetch company group + PAF
            Promise.all([
                getCompanyGroup(root).catch(() => null),
                getPafAnalysis(batchId, itemId).catch(() => null),
            ]).then(([pj, paf]) => {
                setPjData(pj);
                setPafData(paf);
            }).finally(() => setLoading(false));
        }
    }, [batchId, itemId, root, isCPF]);

    // Find the specific branch that matches the item's document
    const currentBranch = pjData?.branches.find(b => b.cnpj.replace(/\D/g, '') === cleanDoc);
    const isMatrix = pjData?.parent_company?.cnpj.replace(/\D/g, '') === cleanDoc;
    const otherBranches = pjData?.branches.filter(b => b.cnpj.replace(/\D/g, '') !== cleanDoc) || [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[95vw] max-w-[1400px] max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700 flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold dark:text-white">{draweeName || pafData?.generalData.name || pjData?.parent_company?.razao_social || pfRegistration?.consumerName || document}</h2>
                        <p className="text-sm text-gray-500 font-mono">{isCPF ? cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : maskCnpj(cleanDoc)} — {isCPF ? 'Pessoa Física' : isMatrix ? 'Matriz' : 'Filial'}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"><FaTimes size={18} /></button>
                </div>

                {/* Tabs */}
                <div className="flex border-b dark:border-gray-700 px-6 flex-shrink-0">
                    {!isCPF && (
                        <button onClick={() => setTab('financeiro')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'financeiro' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                            <FaChartBar className="inline mr-1.5" />Análise Financeira
                        </button>
                    )}
                    <button onClick={() => setTab('cadastro')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'cadastro' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                        <FaBuilding className="inline mr-1.5" />{isCPF ? 'Dados do Bureau' : 'Dados da Receita'}
                    </button>
                    {isCPF && (
                        <button onClick={() => {
                            setTab('empresas');
                            if (partnerCompanies === null) {
                                setLoadingPartners(true);
                                const firstName = (draweeName || pfRegistration?.consumerName || '').split(' ')[0];
                                searchPartnerCompanies(cleanDoc, firstName)
                                    .then(setPartnerCompanies)
                                    .catch(() => setPartnerCompanies([]))
                                    .finally(() => setLoadingPartners(false));
                            }
                        }} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'empresas' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                            <FaBuilding className="inline mr-1.5" />Empresas segundo a Receita
                        </button>
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><FaSpinner className="animate-spin text-2xl text-blue-500" /></div>
                    ) : isCPF ? (
                        /* PF Content */
                        tab === 'empresas' ? (
                            <div className="space-y-4">
                                {loadingPartners ? (
                                    <div className="flex items-center justify-center py-12"><FaSpinner className="animate-spin text-2xl text-blue-500" /></div>
                                ) : partnerCompanies && partnerCompanies.length > 0 ? (
                                    <div>
                                        <p className="text-sm text-gray-500 mb-3">{partnerCompanies.length} empresa(s) encontrada(s)</p>
                                        <table className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded">
                                            <thead className="bg-gray-50 dark:bg-slate-700">
                                                <tr>
                                                    <th className="text-left px-3 py-2">CNPJ</th>
                                                    <th className="text-left px-3 py-2">Razão Social</th>
                                                    <th className="text-left px-3 py-2">Qualificação</th>
                                                    <th className="text-left px-3 py-2">Entrada</th>
                                                    <th className="text-left px-3 py-2">Situação</th>
                                                    <th className="text-left px-3 py-2">UF</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 dark:divide-slate-600">
                                                {partnerCompanies.map((c, i) => {
                                                    const maskedCpf = `***${cleanDoc.slice(3, 9)}**`;
                                                    const socio = (c as any).qsa?.find((s: any) => s.cnpj_cpf_do_socio === maskedCpf);
                                                    return (
                                                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                                                        <td className="px-3 py-2 font-mono text-xs">{c.cnpj}</td>
                                                        <td className="px-3 py-2">{c.razao_social}</td>
                                                        <td className="px-3 py-2 text-xs">{socio?.qualificacao_socio || c.qualificacao_socio || '-'}</td>
                                                        <td className="px-3 py-2 text-xs">{socio?.data_entrada_sociedade || c.data_entrada_sociedade || '-'}</td>
                                                        <td className="px-3 py-2 text-xs">{c.descricao_situacao_cadastral}</td>
                                                        <td className="px-3 py-2">{c.uf}</td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-gray-500 text-center py-8">Nenhuma empresa encontrada para este CPF.</div>
                                )}
                            </div>
                        ) : (
                        <div className="space-y-4">
                            {pfRegistration ? (
                                <div className="border-2 border-green-300 dark:border-green-600 bg-green-50 dark:bg-slate-700 rounded p-4">
                                    <span className="text-xs font-bold text-green-600 dark:text-green-400 uppercase mb-2 block">
                                        Pessoa Física — {pfRegistration._bureau || 'Bureau'}
                                    </span>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0">
                                        {renderField('Nome', pfRegistration.consumerName)}
                                        {renderField('CPF', pfRegistration.documentNumber || cleanDoc)}
                                        {renderField('Nome da Mãe', pfRegistration.motherName)}
                                        {renderField('Data de Nascimento', pfRegistration.birthDate)}
                                        {renderField('Sexo', pfRegistration.consumerGenderDescription || pfRegistration.consumerGender)}
                                        {renderField('Situação CPF', pfRegistration.statusRegistration)}
                                        {renderField('Data Situação', pfRegistration.statusDate)}
                                    </div>
                                    {pfRegistration._score && (
                                        <div className="mt-3 p-3 bg-white dark:bg-slate-800 rounded border border-green-200 dark:border-green-700">
                                            <div className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase mb-1">Score</div>
                                            <div className="grid grid-cols-3 gap-4 text-sm">
                                                {renderField('Score', pfRegistration._score.score)}
                                                {renderField('Probabilidade Default', pfRegistration._score.defaultRate || pfRegistration._score.probabilityOfDefault)}
                                                {renderField('Classe', pfRegistration._score.scoreClass || pfRegistration._score.riskClass)}
                                            </div>
                                        </div>
                                    )}
                                    {pfRegistration.address && (
                                        <div className="mt-3">
                                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase mb-1">Endereço</div>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4">
                                                {renderField('Logradouro', pfRegistration.address.addressLine)}
                                                {renderField('Bairro', pfRegistration.address.district)}
                                                {renderField('Cidade/UF', `${pfRegistration.address.city || ''}/${pfRegistration.address.state || ''}`)}
                                                {renderField('CEP', pfRegistration.address.zipCode)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-gray-500 text-center py-8">Dados de pessoa física não disponíveis. Bureau ainda não foi consultado.</div>
                            )}
                        </div>
                        )
                    ) : tab === 'cadastro' ? (
                        <div className="space-y-4">
                            {/* Current branch highlighted */}
                            {!isMatrix && currentBranch && (
                                <CompanyCard company={currentBranch} badge="Sacado (Filial)" borderColor="border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-slate-700" />
                            )}
                            {/* Matrix */}
                            {pjData?.parent_company && (
                                <CompanyCard company={pjData.parent_company} badge={isMatrix ? 'Sacado (Matriz)' : 'Matriz'} borderColor={isMatrix ? 'border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-slate-700' : 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-slate-700'} />
                            )}
                            {/* Other branches */}
                            {otherBranches.length > 0 && (
                                <div>
                                    <button onClick={() => setShowBranches(p => !p)} className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600">
                                        <span className={`transform transition-transform ${showBranches ? 'rotate-90' : ''}`}>▶</span>
                                        Outras Filiais ({otherBranches.length})
                                    </button>
                                    {showBranches && (
                                        <div className="mt-3 space-y-3">
                                            {otherBranches.map(b => <CompanyCard key={b.cnpj} company={b} badge="Filial" borderColor="border-gray-200 dark:border-slate-600" />)}
                                        </div>
                                    )}
                                </div>
                            )}
                            {!pjData && <div className="text-gray-500 text-center py-8">Dados cadastrais não disponíveis.</div>}
                        </div>
                    ) : (
                        pafData ? <FinancialTab paf={pafData} /> : <div className="text-gray-500 text-center py-8">Dados financeiros não disponíveis.</div>
                    )}
                </div>
            </div>
        </div>
    );
}
